<template>
	<div class="upsell-rules-container" data-gtm-upsell-rules-container="true" aria-labelledby="upsell-title">
		<template v-if="loading">
			<v-layout align-center column>
				<div class="thanks-message">{{$t("shopComponents.gt2.subscriptionPlansWrapper.refreshing")}}</div>
				<v-progress-circular indeterminate color="white"></v-progress-circular>
			</v-layout>
		</template>
		<v-container v-else>
			<v-layout v-if="!isBasketStepper && !isBasketStepperMobile" class="top-text">
				<h2 id="upsell-title">{{ getModalTitle() }}</h2>
			</v-layout>
			<v-layout class="checkbox-list flex-wrap">
				<v-flex
					v-for="product in upsellProducts"
					md4
					class="list-container p-3"
					:key="product.id"
					@click="checkSelectedProducts(product.id)"
					:class="{'selected': selectedUpsellProducts.includes(product.id)}"
					:aria-selected="selectedUpsellProducts.includes(product.id)"
				>
					<template v-if="isNotMobile">
						<div :style="'background-image: url(' + getProductImage(product) + ')'" class="upsell-bg-image">
							<img
								:src="getProductImage(product)"
								:alt="product.name | translate(`product.${product.id}.name`)"
								:class="{'border-yellow': selectedUpsellProducts.includes(product.id)}"
							>
							<LabelComponent
								:product="product"
								parentComponent="upsell"
								v-if="productHasCustomLabelFn(product)"
							/>
							<div
								class="product"
								:class="{'border-yellow': selectedUpsellProducts.includes(product.id)}"
								:data-gtm-upsell-product="selectedUpsellProducts.includes(product.id)"
								:aria-pressed="selectedUpsellProducts.includes(product.id)"
							>
								<p class="product-name" data-gtm-product-name="true">{{product.name | translate(`product.${product.id}.name`)}}</p>
								<div class="product-text">
									<p v-if="!isBM100" class="product-ingredients">
										{{product.description | translate(`product.${product.id}.description`)}}
									</p>
									<div v-if="!isBM100" class="pt-2">
										<p class="product-price product-price-cj">{{ $t(`shopComponents.defaultGlobalTemplate.upsellModal.only`) }}</p>
										<p class="product-price" data-gtm-product-price="true">{{formatPriceZero(discountPrice(product))}}</p>
									</div>
									<div v-else :class="product.name.length < 25 ? 'pt-3' : ''">
										<p class="product-price" data-gtm-product-price="true">{{formatPriceZero(discountPrice(product))}}</p>
									</div>
								</div>
							</div>
						</div>
					</template>
					<template v-else>
						<div class="upsell-bg-image">
							<div v-if="productHasCustomLabelFn(product)">
								<LabelComponent
									:product="product"
									parentComponent="upsell"
								>
									<img
										:src="getProductImage(product)"
										:alt="product.name | translate(`product.${product.id}.name`)"
										:class="{'border-yellow': selectedUpsellProducts.includes(product.id)}"
									>
								</LabelComponent>
							</div>
							<template v-else>
								<img
									:src="getProductImage(product)"
									:alt="product.name | translate(`product.${product.id}.name`)"
									:class="{'border-yellow': selectedUpsellProducts.includes(product.id)}"
								>
							</template>
							<div
								class="product"
								:class="{'border-yellow': selectedUpsellProducts.includes(product.id)}"
								:data-gtm-upsell-product="selectedUpsellProducts.includes(product.id)"
								:aria-pressed="selectedUpsellProducts.includes(product.id)"
							>
								<p class="product-name" data-gtm-product-name="true">{{product.name | translate(`product.${product.id}.name`)}}</p>
								<div class="product-text">
									<p v-if="!isBM100" class="product-ingredients">
										{{product.description | translate(`product.${product.id}.description`)}}
									</p>
									<div v-if="!isBM100" class="pt-2">
										<p class="product-price product-price-cj">{{ $t(`shopComponents.defaultGlobalTemplate.upsellModal.only`) }}</p>
										<p class="product-price" data-gtm-product-price="true">{{formatPriceZero(discountPrice(product))}}</p>
									</div>
									<div v-else :class="!isBasketStepperMobile && product.name.length < 25 ? 'pt-3' : ''">
										<p class="product-price" data-gtm-product-price="true">{{formatPriceZero(discountPrice(product))}}</p>
									</div>
								</div>
							</div>
						</div>
					</template>
					<v-divider v-if="isBasketStepperMobile"></v-divider>
				</v-flex>
				<div class="no-upsell-products" v-if="!upsellProducts.length">{{ $t(`shopComponents.defaultGlobalTemplate.upsellModal.noProducts`) }}</div>
			</v-layout>
			<v-layout class="buttons-container pt-3" :class="isBM100 ? 'justify-content-center' : ''">
				<v-flex v-if="!isBasketStepper && !isBasketStepperMobile" md6 class="px-1">
					<v-btn
						class="back-btn cancel full-width"
						@click="closeDialog"
						data-cy="upsell-cancel-button"
						:key="isUpsellProductAdded"
						:aria-label="closeButtonText"
					>
						{{ closeButtonText }}
					</v-btn>
				</v-flex>
				<v-flex v-if="!isBM100" md6 class="px-1">
					<v-btn
						class="add-btn add full-width"
						@click="addToBasket"
						data-gtm-add-to-basket-upsell="true"
						:aria-label="$t(`shopComponents.defaultGlobalTemplate.upsellModal.addButton`)"
					>
						{{ $t(`shopComponents.defaultGlobalTemplate.upsellModal.addButton`) }}
					</v-btn>
				</v-flex>
			</v-layout>
		</v-container>
		<UpgradeToMenuDialog
			v-if="menuUpgradeDialog.open"
			v-bind="getDialogData('defaultTileGt3')"
		/>
	</div>
</template>

<script>
	import {mapGetters} from "vuex";
	import {
		canBePickedUp,
		copyObject,
		forEachAsync,
		getBackImages,
		getStaticPath,
		RCMSourceType,
		transformProductsToItems
	} from "../../../libraries/utils";
	import ProductSelection from "../../../domains2/orders/ProductSelection";
	import {intersection, shuffle, cloneDeep} from "lodash";
	import {formatPriceZero, isMenu} from "../../../domains2/libraries/Utils";
	import {getProductPriceWithDiscounts} from "../../../libraries/globalTemplateSpecific/bm100/utils";
	import appConfig, {channelEnum} from "../../../appconfig";
	import {
		productHasCustomLabel
	} from "../../../helper/ProductHelper";
	import {gtmTrackAddToBasketFromUpsellRules} from "../../../integrations/tracking/gtm/pages/upsellRules.Gtm";
	import HelloRetail from "../../../integrations/recommendations/helloRetail/HelloRetail";
	import LabelComponent from "../../UtilityComponents/Label";
	import RecommendationEngineProvider from "../../../providers/RecommendationEngineProvider";
	import {DeliveryMethodsEnum} from "../../../domains2/libraries/DeliveryMethods";
	import {getProductIdsFromOrderLines} from "../../../helper/ProductSelectionHelper";
	import pricesMixin from "../../../mixins/prices.mixin";
	import productCatalogMenuMixin from "../../../mixins/productCatalogMenu.mixin";
	import {
		ADD_TO_CART_SINGLE_ITEM,
		CHECKOUT_RESET_WITH_LAST_UPDATE_TIME,
		SET_EXPERIMENT_ID,
		SET_PICKED_RECOMMENDED_PRODUCTS,
		SET_RECOMMENDED_PRODUCTS
	} from "../../../store/mutation-types";
	import upgradeToMenuDialogMixin from "../../../mixins/upgradeToMenuDialog.mixin";
	import {redirectToProductConfiguration} from "../../../helper/ProductConfigurationRedirectionHelper";
	import {getMenuItem} from "../../../helper/productHelper/ProductItemHelper";

	export default {
		name: "UpsellRules",
		props: ["onCloseDialog", "providedProductsMap", "upsellFlow", "isBasketStepper", "isBasketStepperMobile"],
		mixins: [pricesMixin, productCatalogMenuMixin, upgradeToMenuDialogMixin],
		data() {
			return {
				selectedUpsellProducts: [],
				upsellProducts: [],
				recommendationsWithBuffer: [],
				loading: false,
				isUpsellProductAdded: false,
				item: null,
				menuItem: null
			};
		},
		components: {LabelComponent},
		computed: {
			...mapGetters([
				"featureToggles",
				"productCatalog",
				"upsellRulesProducts",
				"cartProducts",
				"orderLines",
				"globalSelectedShop",
				"upsellRulesMaximumNumber",
				"tenantSpecificData",
				"recommendedProducts",
				"pickedRecommendedProducts",
				"tenantSettings",
				"useRecommendations",
				"experimentId",
				"cachedPickupRules"
			]),
			closeButtonText() {
				if(this.providedProductsMap) {
					return this.$t(`shopComponents.defaultGlobalTemplate.upsellModal.marketingCardModalClose`);
				}
				return !this.isUpsellProductAdded
					? this.$t(`shopComponents.defaultGlobalTemplate.upsellModal.closeButton`)
					: this.$t(`shopComponents.defaultGlobalTemplate.upsellModal.goToPayment`);
			},
			staticPath() {
				return getStaticPath();
			},
			isNotMobile() {
				return !(appConfig.channelAlias() === "mobileBrowser" || appConfig.channelAlias(0) === "mobileApp");
			},
			isBM100() {
				return appConfig.isBM100();
			},
			isKioskChannel() {
				return appConfig.channelAlias() === channelEnum.kiosk;
			}
		},
		async mounted() {
			const recommendedProductsMap = {};
			this.loading = true;

			if(!this.providedProductsMap && this.useRecommendations) {
				const productToRcm = getProductIdsFromOrderLines(this.orderLines).map(id => {
					try {
						const product = this.productCatalog.getProduct(id);
						return {
							itemNumber: product.id,
							name: product.name
						};
					} catch (e) {
						// product is not available, should be ignored
						return false;
					}
				}).filter(Boolean);

				const {recommendations, buffer, experimentId} = await RecommendationEngineProvider.getRecommendations({
					deliveryMethod: Object.keys(DeliveryMethodsEnum)
						.find(key => DeliveryMethodsEnum[key] === this.tenantSpecificData.deliveryChoice),
					products: productToRcm,
					shopName: this.globalSelectedShop.shopNameAlias,
					channel: appConfig.channelAlias(),
					experimentType: this.isBasketStepperMobile ? "Basket" : "CrossSell"
				}, appConfig.tenantId);
				const rawRecommendationsWithBuffer = [
					...(recommendations ? recommendations : []),
					...(buffer ? buffer : [])
				];
				this.recommendationsWithBuffer = rawRecommendationsWithBuffer;
				const recommendedProducts = rawRecommendationsWithBuffer.map(item => {
					// returned products could not be available in Bifrost
					// these should be skipped
					try {
						const product = this.productCatalog.getProduct(item.itemNumber);
						const isProductAvailable = canBePickedUp(
							product,
							this.productCatalog,
							this.cachedPickupRules,
							this.globalSelectedShop.id
						);
						return isProductAvailable ? product : undefined;
					} catch (e) {
						return undefined;
					}
				})
					.filter(Boolean);
				if(experimentId && recommendedProducts.length) {
					this.$store.commit(SET_EXPERIMENT_ID, experimentId);
				}
				// we need a map to match the format needed bellow
				recommendedProducts.forEach(item => recommendedProductsMap[item.id] = null);
			}
			const self = this;
			const upselling = this.providedProductsMap || (this.useRecommendations ? recommendedProductsMap : self.upsellRulesProducts);
			const maximumNumber = this.useRecommendations ? (this.tenantSettings.maxRecommendedProducts || 6) : self.upsellRulesMaximumNumber;
			const cartProductIds = self.cartProducts.map(({ productId }) => productId);
			const cartProductDepartments = [];
			cartProductIds.forEach((i) => cartProductDepartments.push(self.productCatalog.getProduct(i).departmentId));
			const cartProductIdsSet = new Set(cartProductIds);
			const upsellProductsForSell = [];
			for (const productId in upselling) {
				const productDepartments = upselling[productId];
				if (!cartProductIdsSet.has(productId) && (!productDepartments || !productDepartments.length || intersection(cartProductDepartments, productDepartments).length)) {
					upsellProductsForSell.push(productId);
				}
			}
			this.loading = false;
			self.convertProductIdsToProducts(upsellProductsForSell, maximumNumber);
			self.selectedUpsellProducts = [];
		},
		methods: {
			performCartOperation(item) {
				item.productSelections.push({
					...ProductSelection.createFromProduct(this.productCatalog.getProduct(item.product.id, true)),
					quantity: 1
				});
				redirectToProductConfiguration(this, {item, upsellFlow: this.$props.upsellFlow});
				this.onCloseDialog({ goToPayment: false });
			},
			async selectMenu() {
				const product = this.productCatalog.getProduct(this.menuItem.product.id);
				const menuItem = transformProductsToItems([product], this.productCatalog, this, this.tenantSpecificData.deliveryChoice)[0];
				menuItem.productSelections.push(this.createCompositeSelectedProducts(menuItem));
				redirectToProductConfiguration(this, {item: menuItem, upsellFlow: this.$props.upsellFlow});
				this.onCloseDialog({ goToPayment: false });
			},
			backImg(product) {
				return getBackImages(product, this.isKioskChannel);
			},
			getModalTitle() {
				if(this.providedProductsMap) {
					return this.$t(`shopComponents.defaultGlobalTemplate.upsellModal.marketingCardModalTitle`);
				}
				return this.$t(`shopComponents.defaultGlobalTemplate.upsellModal.title`);
			},
			formatPriceZero,
			productHasCustomLabelFn(product){
				return productHasCustomLabel(product);
			},
			getProductImage(product, isKiosk){
				const specificProduct = product.id
					? product
					: this.catalogProducts
						.find(element => element.id === product.id_ref);

				// taking only first image due to design breaking with multiple images available
				// getBackImages should order based on is it kiosk or not
				return getBackImages(specificProduct, isKiosk).slice(0, 1);
			},
			handleMenuProducts(item) {
				item.productSelections.push(this.createCompositeSelectedProducts(item));
				redirectToProductConfiguration(this, {item, upsellFlow: this.$props.upsellFlow});
			},
			handleProductsWithConfigurations(item, id) {
				item.productSelections.push({
					...ProductSelection.createFromProduct(this.productCatalog.getProduct(id, true)),
					quantity: 1
				});
				redirectToProductConfiguration(this, {item, upsellFlow: this.$props.upsellFlow});
			},
			checkSelectedProducts(id) {
				const self = this;
				if(self.selectedUpsellProducts.includes(id)) {
					if (!this.featureToggles.verticalKioskBasket) {
						self.selectedUpsellProducts = self.selectedUpsellProducts.filter( (x) => x !== id);
					}
				} else {
					self.selectedUpsellProducts.push(id);
					if(self.isBM100) {
						if(this.useRecommendations) {
							const pickedRecommendation = this.recommendedProducts.find(p => p.itemNumber === id);
							if(pickedRecommendation && pickedRecommendation.trackingCode) {
								HelloRetail.clickTracking(this, pickedRecommendation.trackingCode);
							}
							this.$store.commit(
								SET_PICKED_RECOMMENDED_PRODUCTS,[...this.pickedRecommendedProducts, pickedRecommendation]
							);
						}
						if(this.isNotMobile) {
							const product = this.productCatalog.getProduct(id);
							const item = transformProductsToItems([product], this.productCatalog, this, this.tenantSpecificData.deliveryChoice)[0];
							this.item = item;
							if(item.hasGroupsOrConfigurations) {
								if (isMenu(this.productCatalog, id)) {
									this.handleMenuProducts(item);
								} else if (product.groups.length || product.configurations.length) {
									this.menuItem = getMenuItem(item.product.id, this);
									if (this.menuItem) {
										this.menuUpgradeDialog.open = true;
										return;
									}
									this.handleProductsWithConfigurations(item, id);
								}
								this.onCloseDialog({goToPayment: false});
								return;
							}
						}
						self.addToBasket();
						if (!this.featureToggles.verticalKioskBasket) {
							self.upsellProducts = self.upsellProducts.filter((p) => {
								return p.id !== id;
							});
						}
						if(!this.featureToggles.verticalKioskBasket && self.upsellProducts.length === 0) {
							this.onCloseDialog();
						}
						if (this.featureToggles.verticalKioskBasket && self.upsellProducts.length === self.selectedUpsellProducts.length) {
							this.onCloseDialog();
						}
					}
				}
			},
			copyObject,
			closeDialog() {
				if(this.useRecommendations && this.recommendedProducts.length && this.experimentId) {
					this.$store.commit(SET_RECOMMENDED_PRODUCTS);
					this.$store.commit(SET_PICKED_RECOMMENDED_PRODUCTS);
					this.$store.commit(SET_EXPERIMENT_ID);
				}
				this.onCloseDialog();
			},
			async addToBasket() {
				this.$emit("addToBasketClicked", true);
				const self = this;
				if (!self.selectedUpsellProducts.length) {
					this.onCloseDialog();
					return;
				}
				const validProducts = self.selectedUpsellProducts.map((prodId) => {
					const item = self.productCatalog.getProduct(prodId);
					const productSelection = item.departmentId === 2
						? this.createCompositeSelectedProducts(item.product ? item : {product: item})
						: ProductSelection.createFromProduct(item);
					gtmTrackAddToBasketFromUpsellRules(this, {productSelections: [productSelection], item, upsellFlow: this.$props.upsellFlow});
					HelloRetail.cartTracking(this);
					return {
						productId: prodId,
						quantity: 1,
						groups: productSelection.groups,
						configurations: productSelection.configurations,
						forceNewInCart: true,
						productSelectionId: productSelection.productSelectionId,
						rcmSource: RCMSourceType.CrossSell
					};
				});
				await forEachAsync(validProducts, ps => {
					ps.forceNewInCart = true;
					return self.$store.dispatch(ADD_TO_CART_SINGLE_ITEM, cloneDeep(ps));
				});
				await self.$store.dispatch(CHECKOUT_RESET_WITH_LAST_UPDATE_TIME);
				if (!self.featureToggles.verticalKioskBasket) {
					self.selectedUpsellProducts = [];
				}
				if(!self.isBM100) {
					self.onCloseDialog();
				}
				this.isUpsellProductAdded = true;
			},
			convertProductIdsToProducts(ids, maxNumber) {
				const self = this;
				self.upsellProducts = [];
				ids.forEach((productId) => {
					const categoriesOfProduct = self.productCatalog.getAllCategoriesOfProduct(productId);
					const isProductCategoriesContainSelectedShop = categoriesOfProduct.some(
						(category) => Array.isArray(category.isSpecificForShops) && category.isSpecificForShops.includes(this.globalSelectedShop.id));
					const isProductSupportedByActiveChannel = !categoriesOfProduct.length || categoriesOfProduct.filter(category => !category.channels || category.channels.includes(appConfig.channelAlias())).length;
					// this code covers using only products which are assigned to categories for specified channel
					if(isProductSupportedByActiveChannel && isProductCategoriesContainSelectedShop) {
						self.upsellProducts.push(self.productCatalog.getProduct(productId));
					}
				});
				const productSelectionsFromUpsellProducts = self.upsellProducts.map(product => {
					const productSelection = ProductSelection.createFromProduct(product);
					return {
						...productSelection,
						quantity: 1
					};
				});
				const productPricesDict = getProductPriceWithDiscounts(self.upsellProducts, productSelectionsFromUpsellProducts, self.productCatalog);

				self.upsellProducts = self.upsellProducts.reduce((result, product) => {
					// this filters products based on delivery method
					if(product.options.deliveryMethodIndex) {
						if(!product.options.deliveryMethodIndex.includes(self.tenantSpecificData.deliveryChoice)) {
							return result;
						}
					}
					product.productPrice  = productPricesDict[product.id];
					return [...result, product];
				}, []);

				if (maxNumber && maxNumber < self.upsellProducts.length) {
					self.upsellProducts = this.useRecommendations
						? self.upsellProducts.slice(0, maxNumber)
						: shuffle(self.upsellProducts).slice(0, maxNumber);
				}
				if(!self.upsellProducts.length) {
					this.onCloseDialog();
				}
				if(this.useRecommendations && this.recommendationsWithBuffer.length) {
					this.$store.commit(SET_RECOMMENDED_PRODUCTS, self.upsellProducts.map((p) => {
						const recommendedProduct = this.recommendationsWithBuffer.find(r => r.itemNumber === p.id);
						return {
							itemNumber: recommendedProduct.itemNumber,
							type: recommendedProduct.type,
							trackingCode: recommendedProduct.trackingCode
						};
					})
					);
				}
			}
		}
	};
</script>

<style scoped lang="scss">
	.justify-content-center {
		justify-content: center;
	}
	.list-container{
		padding: 10px 5px;
	}
	.background-surface {
		display: none;
	}
</style>
